// Ported from deexoptions-mobile-app/utils/activity/filters.ts — keep the two
// apps in sync. Spec: deexoptions-mobile-app/docs/activity-system.md

export type ActivityType =
  | "crypto"
  | "giftcard"
  | "billpayment"
  | "payout"
  | "card";

export type ActivityStatus = "COMPLETED" | "FAILED" | "PENDING";

export type ActivityDatePreset = "all" | "1w" | "1m" | "3m" | "custom";

export interface ActivityFilter {
  type: ActivityType;
  subtype?: string;
  status?: ActivityStatus;
  startDate?: string;
  endDate?: string;
  coin?: string;
}

export interface ActivityTypeConfig {
  key: ActivityType;
  label: string;
  subtypes: { key: string; label: string }[] | null;
  defaultSubtype?: string;
  live: boolean;
}

export const ACTIVITY_TYPES: ActivityTypeConfig[] = [
  {
    key: "crypto",
    label: "Crypto",
    subtypes: [
      { key: "all", label: "All" },
      { key: "sell", label: "Sell" },
      { key: "deposit", label: "Deposit" },
      { key: "swap", label: "Swap" },
      { key: "withdraw", label: "Withdraw" },
    ],
    defaultSubtype: "all",
    live: true,
  },
  {
    key: "giftcard",
    label: "Gift cards",
    subtypes: null,
    live: true,
  },
  {
    key: "billpayment",
    label: "Bills",
    subtypes: [
      { key: "airtime", label: "Airtime" },
      { key: "data", label: "Data" },
      { key: "electricity", label: "Electricity" },
      { key: "betting", label: "Betting" },
    ],
    defaultSubtype: "airtime",
    live: true,
  },
  {
    key: "payout",
    label: "Payout",
    subtypes: null,
    live: true,
  },
  {
    // Card product is not live yet — flip `live` when it ships.
    key: "card",
    label: "Card",
    subtypes: [
      { key: "top-up", label: "Top-up" },
      { key: "fee", label: "Fee" },
      { key: "withdrawal", label: "Withdrawal" },
      { key: "payment", label: "Payment" },
      { key: "card-creation", label: "Card creation" },
    ],
    defaultSubtype: "top-up",
    live: false,
  },
];

export const LIVE_ACTIVITY_TYPES = ACTIVITY_TYPES.filter((t) => t.live);

export const getActivityTypeConfig = (type: ActivityType) =>
  ACTIVITY_TYPES.find((t) => t.key === type);

// Types with subtypes must always send a subtype — this is the single place
// that rule is enforced.
export const withDefaultSubtype = (
  type: ActivityType,
  subtype?: string
): Pick<ActivityFilter, "type" | "subtype"> => {
  const config = getActivityTypeConfig(type);
  if (!config?.subtypes) return { type };
  const valid = subtype && config.subtypes.some((s) => s.key === subtype);
  return { type, subtype: valid ? subtype : config.defaultSubtype };
};

export const DEFAULT_ACTIVITY_FILTER: ActivityFilter =
  withDefaultSubtype("crypto");

/** Swap-only filter, shared by the swap screen's recent list and swap history. */
export const SWAP_FILTER: ActivityFilter = { type: "crypto", subtype: "swap" };

export const getDateRange = (
  preset: Exclude<ActivityDatePreset, "all" | "custom">
): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date(end);
  if (preset === "1w") start.setDate(end.getDate() - 7);
  if (preset === "1m") start.setMonth(end.getMonth() - 1);
  if (preset === "3m") start.setMonth(end.getMonth() - 3);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

export const buildTransactionsQuery = (
  filter: ActivityFilter,
  // Cursor pagination: `cursor` is the opaque `pageInfo.nextCursor` from the
  // previous page, passed back as `?cursor=`. Undefined/empty on the first page.
  // The param is `cursor`, not `page` — the backend rejects `page` outright
  // ("page is not allowed", HTTP 400), so sending it broke every page after
  // the first on both staging and production.
  cursor?: string,
  limit?: number
) => {
  const params = new URLSearchParams();
  params.append("type", filter.type);
  if (filter.subtype) params.append("subtype", filter.subtype);
  if (filter.status) params.append("status", filter.status);
  if (filter.startDate) params.append("startDate", filter.startDate);
  if (filter.endDate) params.append("endDate", filter.endDate);
  if (filter.coin) params.append("coin", filter.coin.toLowerCase());
  if (cursor) params.append("cursor", cursor);
  if (limit !== undefined) params.append("limit", String(limit));
  return params.toString();
};

// The summary endpoint takes the same filter minus status/page/limit.
export const buildSummaryQuery = (filter: ActivityFilter) =>
  buildTransactionsQuery({ ...filter, status: undefined });
